import { Fragment } from 'react';
import { type Components } from 'react-markdown';

import { Badge } from '@apps/web/components/ui/badge';
import { Markdown } from '@apps/web/components/ui/markdown';
import { type components } from '@libraries/typescript/api_client';

type PlaybookItem = components['schemas']['AgentPlaybookItemRead'];

export type PlaybookSurface = PlaybookItem['applies_to'][number];

export const PLAYBOOK_SURFACE_OPTIONS: Array<{
    readonly value: PlaybookSurface;
    readonly label: string;
    readonly hasDedicatedEditor?: boolean;
}> = [
    {
        value: 'activity_feed',
        label: 'Activity feed items',
        hasDedicatedEditor: true,
    },
    { value: 'callout', label: 'Outreach' },
    { value: 'general', label: 'General chat' },
    { value: 'inbound_voice', label: 'Inbound calls' },
    { value: 'outbound_voice', label: 'Outbound calls' },
    { value: 'sms_shift_cancellation', label: 'SMS cancellations' },
    { value: 'inbound_cancellation', label: 'Inbound cancellations' },
];

const EMPTY_RULES_MARKDOWN = [
    '_No rules yet._',
    '',
    'Add your first rule below.',
].join('\n');

const RULES_MARKDOWN_COMPONENTS: Components = {
    h1: ({ node, ...props }) => (
        <h1
            className="mt-5 mb-1.5 text-xl leading-tight font-bold"
            {...props}
        />
    ),
    h2: ({ node, ...props }) => (
        <h2
            className="mt-4 mb-1.5 text-lg leading-tight font-semibold"
            {...props}
        />
    ),
    h3: ({ node, ...props }) => (
        <h3
            className="mt-3 mb-1 text-base leading-snug font-semibold"
            {...props}
        />
    ),
    p: ({ node, ...props }) => (
        <p className="my-0.5 text-sm leading-6" {...props} />
    ),
    ul: ({ node, ...props }) => (
        <ul className="my-0.5 list-disc space-y-0.5 pl-5" {...props} />
    ),
    ol: ({ node, ...props }) => (
        <ol className="my-0.5 list-decimal space-y-0.5 pl-5" {...props} />
    ),
    li: ({ node, ...props }) => <li className="text-sm leading-6" {...props} />,
};

export function PlaybookRulesMarkdown({
    bodyMarkdown,
}: {
    readonly bodyMarkdown: string;
}) {
    return (
        <div className="bg-muted/20 rounded-md border p-4">
            <Markdown components={RULES_MARKDOWN_COMPONENTS}>
                {bodyMarkdown || EMPTY_RULES_MARKDOWN}
            </Markdown>
        </div>
    );
}

export function getSurfaceLabel(surface: PlaybookSurface): string {
    return (
        PLAYBOOK_SURFACE_OPTIONS.find((option) => option.value === surface)
            ?.label ?? surface
    );
}

export function formatPlaybookDate(value: string | null): string {
    if (!value) {
        return 'Never';
    }
    return new Intl.DateTimeFormat(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    }).format(new Date(value));
}

export function PlaybookDetailsBody({
    enabled,
    appliesTo,
    createdByUserName,
    updatedByUserName,
    updatedAt,
    bodyMarkdown,
}: {
    readonly enabled: boolean;
    readonly appliesTo: ReadonlyArray<PlaybookSurface>;
    readonly createdByUserName: string | null;
    readonly updatedByUserName: string | null;
    readonly updatedAt: string | null;
    readonly bodyMarkdown: string;
}) {
    return (
        <Fragment>
            {!enabled && (
                <div className="border-border text-muted-foreground rounded-md border border-dashed p-3 text-sm">
                    This playbook is off. Phoebe ignores it until it is turned
                    back on.
                </div>
            )}
            <div className="grid gap-3 text-sm sm:grid-cols-4">
                <div className="rounded-md border p-3">
                    <div className="text-muted-foreground text-xs">
                        Phoebe uses this for
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                        {appliesTo.map((surface) => (
                            <Badge key={surface} variant="secondary">
                                {getSurfaceLabel(surface)}
                            </Badge>
                        ))}
                    </div>
                </div>
                <div className="rounded-md border p-3">
                    <div className="text-muted-foreground text-xs">
                        Added by
                    </div>
                    <div className="mt-2 truncate">
                        {createdByUserName ?? '—'}
                    </div>
                </div>
                <div className="rounded-md border p-3">
                    <div className="text-muted-foreground text-xs">
                        Modified by
                    </div>
                    <div className="mt-2 truncate">
                        {updatedByUserName ?? '—'}
                    </div>
                </div>
                <div className="rounded-md border p-3">
                    <div className="text-muted-foreground text-xs">
                        Updated at
                    </div>
                    <div className="mt-2">{formatPlaybookDate(updatedAt)}</div>
                </div>
            </div>
            <PlaybookRulesMarkdown bodyMarkdown={bodyMarkdown} />
        </Fragment>
    );
}
